import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ScrollText,
  Search,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AuditLog {
  id: string;
  created_at: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  workspace_id: string | null;
  old_values?: Record<string, unknown> | null;
  new_values?: Record<string, unknown> | null;
  performed_by: {
    id: string;
    full_name: string;
    email: string;
    avatar_url?: string | null;
  };
  impersonated_by?: string | null;
  ip_address?: string | null;
}

interface AuditLogsResponse {
  data: AuditLog[];
  pagination: { cursor: string | null; has_more: boolean; total_count: number };
}

interface UsersResponse {
  data: Array<{ id: string; full_name: string }>;
}

const ACTION_OPTIONS = ['create', 'update', 'delete'];
const ENTITY_OPTIONS = ['goal', 'task', 'group', 'team', 'sprint', 'user', 'profile', 'workspace'];

function getActionBadgeVariant(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (action) {
    case 'create': return 'default';
    case 'update': return 'secondary';
    case 'delete': return 'destructive';
    default: return 'outline';
  }
}

function formatDetails(values: Record<string, unknown>): string {
  const title = (values.title || values.name || values.full_name) as string | undefined;
  if (title) return `"${title}"`;
  const keys = Object.keys(values).slice(0, 3);
  if (keys.length === 0) return '';
  return keys.map((k) => `${k}: ${JSON.stringify(values[k]).slice(0, 30)}`).join(', ');
}

export function AuditLogsPage() {
  const [activeTab, setActiveTab] = useState<'all' | 'admin'>('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityType, setEntityType] = useState('all');
  const [userIdFilter, setUserIdFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params: Record<string, string> = {};
  if (actionFilter !== 'all') params.action = actionFilter;
  if (entityType !== 'all') params.entity_type = entityType;
  if (userIdFilter !== 'all') params.user_id = userIdFilter;
  if (appliedSearch) params.search = appliedSearch;
  if (cursor) params.cursor = cursor;

  const { data, isLoading, refetch } = useQuery<AuditLogsResponse>({
    queryKey: ['admin-audit-logs', params, activeTab],
    queryFn: () => api.get('/admin/audit-logs', { params }).then((r) => r.data),
  });

  const { data: usersData } = useQuery<UsersResponse>({
    queryKey: ['admin-users-list'],
    queryFn: () => api.get('/admin/users', { params: { limit: '100' } }).then((r) => r.data),
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;
  const users = usersData?.data ?? [];

  function resetPagination() {
    setCursor(undefined);
    setCursorStack([]);
  }

  function goNext() {
    if (pagination?.cursor) {
      setCursorStack((s) => [...s, cursor ?? '']);
      setCursor(pagination.cursor);
    }
  }

  function goPrev() {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    setCursor(prev || undefined);
  }

  function handleSearch() {
    setAppliedSearch(searchInput);
    resetPagination();
  }

  function clearFilters() {
    setActionFilter('all');
    setEntityType('all');
    setUserIdFilter('all');
    setSearchInput('');
    setAppliedSearch('');
    resetPagination();
  }

  const hasActiveFilters = actionFilter !== 'all' || entityType !== 'all' || userIdFilter !== 'all' || appliedSearch;

  function formatTimestamp(d: string) {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Audit Logs"
        description="Track all activity and changes within your workspace"
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as 'all' | 'admin'); resetPagination(); }}>
          <TabsList>
            <TabsTrigger value="all">All Activity</TabsTrigger>
            <TabsTrigger value="admin">Admin Actions</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="mt-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Filters</CardTitle>
              <CardDescription>Filter logs by action, entity, user, or date range</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); resetPagination(); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {ACTION_OPTIONS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={entityType} onValueChange={(v) => { setEntityType(v); resetPagination(); }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Entity Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entities</SelectItem>
                    {ENTITY_OPTIONS.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={userIdFilter} onValueChange={(v) => { setUserIdFilter(v); resetPagination(); }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="User" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex gap-2">
                  <Input
                    placeholder="Search entity..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-[200px]"
                  />
                  <Button variant="secondary" size="icon" onClick={handleSearch}>
                    <Search className="h-4 w-4" />
                  </Button>
                </div>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <TabsContent value="all" className="mt-4">
            <LogsTable
              logs={logs}
              isLoading={isLoading}
              pagination={pagination}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              formatTimestamp={formatTimestamp}
              goNext={goNext}
              goPrev={goPrev}
              cursorStackLength={cursorStack.length}
            />
          </TabsContent>

          <TabsContent value="admin" className="mt-4">
            <LogsTable
              logs={logs}
              isLoading={isLoading}
              pagination={pagination}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              formatTimestamp={formatTimestamp}
              goNext={goNext}
              goPrev={goPrev}
              cursorStackLength={cursorStack.length}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

interface LogsTableProps {
  logs: AuditLog[];
  isLoading: boolean;
  pagination?: { cursor: string | null; has_more: boolean; total_count: number };
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  formatTimestamp: (d: string) => string;
  goNext: () => void;
  goPrev: () => void;
  cursorStackLength: number;
}

function LogsTable({
  logs,
  isLoading,
  pagination,
  expandedId,
  setExpandedId,
  formatTimestamp,
  goNext,
  goPrev,
  cursorStackLength,
}: LogsTableProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-muted-foreground">
            <ScrollText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No Audit Logs Found</p>
            <p className="text-sm mt-1">No logs match your current filters. Try adjusting your search criteria.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
        <CardDescription>
          Showing {pagination?.total_count ?? logs.length} entries
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]" />
              <TableHead className="w-[180px]">Timestamp</TableHead>
              <TableHead className="w-[100px]">Action</TableHead>
              <TableHead className="w-[100px]">Entity</TableHead>
              <TableHead className="w-[150px]">User</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <Fragment key={log.id}>
                <TableRow
                  className={cn(
                    'cursor-pointer hover:bg-muted/50',
                    expandedId === log.id && 'bg-muted/20',
                  )}
                  onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                >
                  <TableCell className="p-2">
                    {expandedId === log.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronUp className="h-4 w-4 text-muted-foreground rotate-90" />
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatTimestamp(log.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getActionBadgeVariant(log.action)}>{log.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm capitalize">{log.entity_type}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        name={log.performed_by.full_name}
                        avatarUrl={log.performed_by.avatar_url}
                        size="xs"
                      />
                      <div className="min-w-0">
                        <p className="text-sm truncate">{log.performed_by.full_name}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">
                    <div className="truncate">
                      {log.new_values && formatDetails(log.new_values as Record<string, unknown>)}
                    </div>
                  </TableCell>
                </TableRow>
                {expandedId === log.id && (
                  <TableRow key={`${log.id}-detail`}>
                    <TableCell colSpan={6} className="p-0">
                      <div className="m-3 p-4 rounded-lg border bg-muted/30 space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-muted-foreground">Entity ID:</span>
                            <p className="font-mono text-xs mt-1">{log.entity_id || '-'}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">IP Address:</span>
                            <p className="font-mono text-xs mt-1">{log.ip_address || '-'}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Email:</span>
                            <p className="text-xs mt-1">{log.performed_by.email}</p>
                          </div>
                          {log.impersonated_by && (
                            <div>
                              <span className="text-muted-foreground">Impersonated By:</span>
                              <p className="font-mono text-xs mt-1">{log.impersonated_by}</p>
                            </div>
                          )}
                        </div>
                        {log.old_values && Object.keys(log.old_values).length > 0 && (
                          <div>
                            <span className="text-muted-foreground">Previous Values:</span>
                            <pre className="mt-1 p-2 bg-muted rounded font-mono text-xs overflow-auto max-h-40">
                              {JSON.stringify(log.old_values, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.new_values && Object.keys(log.new_values).length > 0 && (
                          <div>
                            <span className="text-muted-foreground">New Values:</span>
                            <pre className="mt-1 p-2 bg-muted rounded font-mono text-xs overflow-auto max-h-40">
                              {JSON.stringify(log.new_values, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination?.total_count ?? 0} total entries
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={cursorStackLength === 0}>
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!pagination?.has_more}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
